import React, { useState } from "react";
import { Box, Button } from "@material-ui/core";
import {
  CTextField,
  CSelect,
  Text,
  HFlex,
  ProgressButton,
  DialogSuccessNotice,
  useErrorContext,
  Loading,
} from "shared";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  HealthCareOrganization,
  useUpdateHealthCareOrganization,
  useCreateStripeSubscription,
  useUpdateStripeSubscription,
  useStripePrices,
} from "../queries/useHealthCareOrganizations";

interface Props {
  hco: HealthCareOrganization;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  platform_fee_model: string;
  platform_fee_cents: string;
  platform_fee_percentage: string;
  seats: string;
  stripe_price_id: string;
}

const platformFeeModelOptions = [
  { value: "flat_fee", label: "Flat Fee" },
  { value: "per_seat", label: "Per Seat" },
  { value: "percentage", label: "Percentage" },
];

export const HcoPricingForm: React.VFC<Props> = ({ hco, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const [success, setSuccess] = useState(false);

  const hasActiveSubscription = hco.stripe_subscription_status === "active";

  const form = useForm<FormData>({
    defaultValues: {
      platform_fee_model: hco.platform_fee_model || "",
      platform_fee_cents: hco.platform_fee_cents != null ? String(hco.platform_fee_cents) : "",
      platform_fee_percentage: hco.platform_fee_bps != null ? String(hco.platform_fee_bps / 100) : "",
      seats: hco.seats != null ? String(hco.seats) : "0",
      stripe_price_id: "",
    },
  });

  const platformFeeModel = useWatch({ control: form.control, name: "platform_fee_model" });
  const isPercentage = platformFeeModel === "percentage";
  const isPerSeat = platformFeeModel === "per_seat";
  const isFlatFee = platformFeeModel === "flat_fee";

  const pricesQuery = useStripePrices(hco.id, isPerSeat);
  const priceOptions = (pricesQuery.data ?? []).map(p => {
    const interval =
      p.recurring_interval_count > 1
        ? `${p.recurring_interval_count} ${p.recurring_interval}s`
        : p.recurring_interval;
    const amount = (p.unit_amount / 100).toFixed(2);
    return {
      value: p.id,
      label: `${p.nickname || p.id} — ${amount} ${p.currency.toUpperCase()} / ${interval}`,
    };
  });

  const updateHco = useUpdateHealthCareOrganization();
  const createSubscription = useCreateStripeSubscription();
  const updateSubscription = useUpdateStripeSubscription();

  const hasSubscription = !!hco.stripe_subscription_id;

  const isLoading = updateHco.isLoading || createSubscription.isLoading || updateSubscription.isLoading;

  const submit = form.handleSubmit(async fields => {
    try {
      const selectedPrice = pricesQuery.data?.find(p => p.id === fields.stripe_price_id);
      const perSeatCents = selectedPrice?.unit_amount ?? hco.platform_fee_cents ?? 0;

      await updateHco.mutateAsync({
        id: hco.id,
        name: hco.name,
        contact_email: hco.contact_email,
        billing_email: hco.billing_email,
        address: hco.address,
        city: hco.city,
        zip_code: hco.zip_code,
        iana_timezone: hco.iana_timezone,
        organization_size_range: hco.organization_size_range,
        country_id: hco.country_id,
        us_state_id: hco.us_state_id,
        platform_fee_model: fields.platform_fee_model || undefined,
        platform_fee_cents: isPercentage
          ? 0
          : isPerSeat
          ? perSeatCents
          : fields.platform_fee_cents
          ? Number(fields.platform_fee_cents)
          : 0,
        platform_fee_bps: isPercentage
          ? fields.platform_fee_percentage
            ? Math.round(Number(fields.platform_fee_percentage) * 100)
            : 0
          : 0,
        seats: isPerSeat && fields.seats ? Number(fields.seats) : 0,
      });

      if (!hasSubscription && (isPerSeat || isFlatFee)) {
        await createSubscription.mutateAsync(
          isPerSeat
            ? { hcoId: hco.id, price_id: selectedPrice?.id, price_cents: selectedPrice?.unit_amount }
            : { hcoId: hco.id, price_cents: fields.platform_fee_cents ? Number(fields.platform_fee_cents) : undefined }
        );
      } else if (hasActiveSubscription) {
        const subscriptionData: { hcoId: number; price_id?: string; price_cents?: number; seats?: number } = {
          hcoId: hco.id,
        };
        if (isPerSeat) {
          if (fields.stripe_price_id) subscriptionData.price_id = fields.stripe_price_id;
          if (selectedPrice) subscriptionData.price_cents = selectedPrice.unit_amount;
          if (fields.seats) subscriptionData.seats = Number(fields.seats);
        } else if (isFlatFee) {
          if (fields.platform_fee_cents) subscriptionData.price_cents = Number(fields.platform_fee_cents);
        }
        await updateSubscription.mutateAsync(subscriptionData);
      }

      setSuccess(true);
    } catch (err) {
      setError(t("errors.could_not_update_health_care_organization"), err);
    }
  });

  if (success) {
    return (
      <DialogSuccessNotice
        heading={t("form.hco_pricing_updated_successfully")}
        body={`${hco.name} (ID: ${hco.id})`}
        onClose={onSuccess}
      />
    );
  }

  return (
    <Box mx={3} my={1}>
      <Text heading bold mb={1}>
        {t("form.hco_edit_pricing")}
      </Text>
      <Text paragraph mb={3}>
        {hco.name}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CSelect
            required
            name="platform_fee_model"
            label={t("form.hco_platform_fee_model")}
            items={platformFeeModelOptions}
            margin="normal"
            size="small"
            fullWidth
            disabled={hasActiveSubscription}
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          {hasActiveSubscription && (
            <Text secondary caption mt={0} mb={1}>
              {t("form.hco_pricing_model_locked_subscription")}
            </Text>
          )}

          {isPercentage && (
            <CTextField
              name="platform_fee_percentage"
              label={t("form.hco_platform_fee_percentage")}
              type="number"
              margin="normal"
              size="small"
              fullWidth
              inputProps={{ step: "0.01", min: "0", max: "100" }}
            />
          )}

          {!isPercentage && !isPerSeat && (
            <CTextField
              name="platform_fee_cents"
              label={t("form.hco_platform_fee_cents")}
              type="number"
              margin="normal"
              size="small"
              fullWidth
            />
          )}

          {isPerSeat && (
            <>
              {pricesQuery.isLoading ? (
                <Loading my={2} />
              ) : (
                <CSelect
                  name="stripe_price_id"
                  label={t("form.hco_stripe_price")}
                  items={priceOptions}
                  margin="normal"
                  size="small"
                  fullWidth
                >
                  {({ label }) => <Text>{label}</Text>}
                </CSelect>
              )}
              <CTextField
                name="seats"
                label={t("form.hco_seats")}
                type="number"
                margin="normal"
                size="small"
                fullWidth
              />
            </>
          )}

          {hasActiveSubscription && (
            <Text secondary caption mt={2} mb={0}>
              {t("form.hco_pricing_subscription_sync_note")}
            </Text>
          )}

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button disabled={isLoading} onClick={onCancel}>
              {t("buttons.cancel")}
            </Button>
            <ProgressButton loading={isLoading}>{t("buttons.save")}</ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};